import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Ticket, MoreVertical, Archive, Trash2, ArchiveRestore } from 'lucide-react';
import CreateTicketModal from '@/components/CreateTicketModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('active'); // 'active' or 'archived'
  const [deletingTicketId, setDeletingTicketId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/support-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (ticketId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/support-tickets/${ticketId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket archived');
      fetchTickets();
    } catch (error) {
      console.error('Error archiving ticket:', error);
      toast.error('Failed to archive ticket');
    }
  };

  const handleUnarchive = async (ticketId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/support-tickets/${ticketId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket unarchived');
      fetchTickets();
    } catch (error) {
      console.error('Error unarchiving ticket:', error);
      toast.error('Failed to unarchive ticket');
    }
  };

  const handleDelete = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket deleted');
      setDeletingTicketId(null);
      fetchTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Failed to delete ticket');
    }
  };

  const handleTicketCreated = (newTicket) => {
    setTickets([newTicket, ...tickets]);
    setShowCreateModal(false);
    toast.success('Support ticket submitted successfully');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Open', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In Progress', class: 'bg-yellow-100 text-yellow-800' },
      resolved: { label: 'Resolved', class: 'bg-green-100 text-green-800' },
      closed: { label: 'Closed', class: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
                Support Tickets
              </h1>
              <p className="text-lg" style={{ color: '#FFFFFF', opacity: 0.9 }}>
                Submit issues, errors, or requests directly to our team.
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="h-12 px-6 text-base font-semibold flex items-center gap-2"
              style={{ backgroundColor: '#D4AF37', color: '#FFFFFF' }}
            >
              <Plus className="h-5 w-5" />
              Create New Ticket
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex gap-2">
          <Button
            onClick={() => setFilter('active')}
            variant={filter === 'active' ? 'default' : 'outline'}
            style={filter === 'active' ? { backgroundColor: '#245451', color: '#FFFFFF' } : {}}
          >
            Active Tickets
          </Button>
          <Button
            onClick={() => setFilter('archived')}
            variant={filter === 'archived' ? 'default' : 'outline'}
            style={filter === 'archived' ? { backgroundColor: '#245451', color: '#FFFFFF' } : {}}
          >
            Archived Tickets
          </Button>
        </div>

        {/* Tickets Table */}
        <Card>
          <CardHeader>
            <CardTitle>Your Support Tickets</CardTitle>
            <CardDescription>Click on any ticket to view details</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <p style={{ color: '#64748B' }}>Loading tickets...</p>
              </div>
            ) : tickets.filter(t => filter === 'archived' ? t.is_archived : !t.is_archived).length === 0 ? (
              <div className="text-center py-12">
                <Ticket className="h-16 w-16 mx-auto mb-4" style={{ color: '#CBD5E1' }} />
                <p className="text-lg font-medium mb-2" style={{ color: '#64748B' }}>
                  No support tickets yet
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  Create your first ticket to get help from our team
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Ticket ID</th>
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Subject</th>
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Category</th>
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Status</th>
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Date Submitted</th>
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Last Updated</th>
                      <th className="text-left p-4 font-semibold" style={{ color: '#245451' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.filter(t => filter === 'archived' ? t.is_archived : !t.is_archived).map((ticket) => (
                      <tr
                        key={ticket.id}
                        className="border-b hover:bg-gray-50 transition-colors"
                      >
                        <td className="p-4">
                          <span className="font-mono text-sm" style={{ color: '#64748B' }}>
                            #{ticket.ticket_id}
                          </span>
                        </td>
                        <td 
                          className="p-4 cursor-pointer"
                          onClick={() => navigate(`/support-tickets/${ticket.id}`)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium" style={{ color: '#245451' }}>
                              {ticket.subject}
                            </span>
                            {ticket.is_archived && (
                              <Badge className="bg-gray-100 text-gray-800 text-xs">Archived</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span style={{ color: '#64748B' }}>{ticket.category}</span>
                        </td>
                        <td className="p-4">
                          {getStatusBadge(ticket.status)}
                        </td>
                        <td className="p-4" style={{ color: '#64748B' }}>
                          {formatDate(ticket.created_at)}
                        </td>
                        <td className="p-4" style={{ color: '#64748B' }}>
                          {formatDate(ticket.updated_at)}
                        </td>
                        <td className="p-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {ticket.is_archived ? (
                                <DropdownMenuItem onClick={(e) => handleUnarchive(ticket.id, e)}>
                                  <ArchiveRestore className="h-4 w-4 mr-2" />
                                  Unarchive
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={(e) => handleArchive(ticket.id, e)}>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingTicketId(ticket.id);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTicketCreated={handleTicketCreated}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletingTicketId !== null} onOpenChange={() => setDeletingTicketId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the ticket and its history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deletingTicketId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default SupportTicketsPage;
